import {Button, ButtonGroup} from "@client/components/Button";
import {Prose} from "@client/components/Prose";
import * as constants from "@client/constants";
import * as subscriptions from "@client/features/subscriptions";
import * as typography from "@client/shared/typography";
import {useAppDispatch, useAppSelector} from "@client/store";
import * as utils from "@client/utils";
import {models, reverse} from "@reactivated";
import * as React from "react";

interface Props {
    subscribe: () => void;
    unsubscribe: () => void;
    showPayPal: () => void;
    subscription: models["core.payments.Subscription"] | null;
    canUsePayPal: boolean;
    isShowingPayPal: boolean;
    subscriptionDollarsPerMonth: number;
    platform: constants.Platform;
}

const AppleSubscriptionDetail = (props: Props) => {
    const dispatch = useAppDispatch();
    const {isOnline, isLoaded, products, ownedProductIds} = useAppSelector((state) => ({
        isOnline: state.scenes.isOnline,
        isLoaded: state.subscriptions.isLoaded,
        products: state.subscriptions.products,
        ownedProductIds: state.subscriptions.ownedProductIds,
    }));
    const PRODUCT_NAME = useAppSelector((state) =>
        subscriptions.getSubscriptionProductName(state, "basic"),
    );
    const product = products.find((candidate) => candidate.id == PRODUCT_NAME);
    const ownedProduct = products.find((candidate) =>
        ownedProductIds.includes(candidate.id),
    );

    if (product == null || isOnline === false || isLoaded === false) {
        return (
            <p>
                Cannot load subscriptions at this time. Make sure you are online. You
                can also try restarting the app.
            </p>
        );
    }

    const common = (
        <>
            <p>
                View our{" "}
                <typography.A href={reverse("privacy_policy")}>
                    Privacy Policy
                </typography.A>{" "}
                and{" "}
                <typography.A href={reverse("terms_of_service")}>
                    Terms of Service
                </typography.A>
            </p>
        </>
    );

    // The device can hold a subscription the server links to another account;
    // Restore moves it here.
    if (ownedProduct != null && props.subscription?.type === "apple") {
        return (
            <Prose>
                <p>
                    You are currently on a {ownedProduct.displayName} paid until{" "}
                    {utils.formatDate(props.subscription.expires_date)}
                </p>
                <p>It renews until you cancel.</p>
                <p>Thank you for being a loyal customer.</p>
                {common}
                <p>
                    <Button onClick={() => void subscriptions.manageSubscriptions()}>
                        Manage Subscription
                    </Button>
                </p>
            </Prose>
        );
    }

    return (
        <Prose>
            <p>
                To continue using Joy after {constants.TRIAL_DAYS} days, you need a
                subscription. It’s just {product.displayPrice} per month.
            </p>
            <p>Paid subscriptions keep Joy ad-free.</p>
            {common}
            <ButtonGroup>
                <Button
                    onClick={() =>
                        void dispatch(subscriptions.purchaseSubscription(product.id))
                    }
                >
                    Purchase {product.displayName}
                </Button>
                <Button onClick={() => void dispatch(subscriptions.restorePurchases())}>
                    Restore Purchases
                </Button>
            </ButtonGroup>
        </Prose>
    );
};

export const SubscriptionDetail = (props: Props) => {
    if (
        props.subscription &&
        props.subscription.type === "stripe" &&
        props.subscription.status === "active"
    ) {
        return (
            <Prose>
                <p>
                    You are currently subscribed. Thank you for being a loyal customer.
                </p>
                <p>
                    Your subscription is currently paid until{" "}
                    {utils.formatDate(props.subscription.current_period_end)}.
                </p>
                <p>On that date, you’ll be billed again.</p>
                <Button href={reverse("billing")} track="billing">
                    Update Billing Information
                </Button>
                <Button onClick={props.unsubscribe}>Unsubscribe</Button>
            </Prose>
        );
    } else if (
        props.subscription &&
        props.subscription.type === "stripe" &&
        props.subscription.status === "past_due"
    ) {
        return (
            <Prose>
                <p>
                    Looks like we had trouble collecting payment. To ensure
                    uninterrupted service, please update your payment details.
                </p>
                <Button onClick={() => props.subscribe()}>Update Payment Info</Button>
            </Prose>
        );
    } else if (
        props.subscription &&
        props.subscription.type === "apple" &&
        props.platform !== "ios"
    ) {
        return (
            <Prose>
                <p>
                    You are currently on an Apple subscription paid until{" "}
                    {utils.formatDate(props.subscription.expires_date)}
                </p>
                <p>You’ll be billed every month until you cancel.</p>
                <p>Thank you for being a loyal customer.</p>
                <Button href="https://support.apple.com/en-us/HT202039">
                    Manage Subscription
                </Button>
            </Prose>
        );
    } else {
        if (props.platform === "ios") {
            return <AppleSubscriptionDetail {...props} />;
        }

        return (
            <Prose>
                <p>
                    To continue using Joy after {constants.TRIAL_DAYS} days, you need a
                    subscription. It’s just {props.subscriptionDollarsPerMonth} bucks a
                    month.
                </p>
                <p>Paid subscriptions help keep Joy ad-free.</p>
                <Button onClick={() => props.subscribe()}>Subscribe</Button>
                {!props.isShowingPayPal && (
                    <Button onClick={props.showPayPal}>
                        I don’t have a credit card or a debit card.
                    </Button>
                )}
                {props.isShowingPayPal &&
                    props.canUsePayPal && [
                        <p key={1}>
                            You can use PayPal to pay yearly by clicking below.
                        </p>,
                        <Button key={2} href="/paypal/">
                            PayPal Checkout
                        </Button>,
                    ]}
                {props.isShowingPayPal && !props.canUsePayPal && (
                    <p>
                        If you want to use PayPal or pay yearly, visit the web app using
                        your browser.
                    </p>
                )}
            </Prose>
        );
    }
};
